import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import StatusChip from '../common/StatusChip';
import { Topic } from '../../services/topics';
import { Planner } from '../../services/planners';
import { formatDate } from '../../utils/formatDate';

interface TopicListProps {
  topics: Topic[];
  planners: Planner[];
  selectedTopicId: string | null;
  onSelect: (topic: Topic) => void;
}

const TopicList: React.FC<TopicListProps> = ({ topics, planners, selectedTopicId, onSelect }) => {
  const plannerMap = Object.fromEntries(planners.map((p) => [p.id, p]));

  const sortedTopics = useMemo(() => {
    return [...topics].sort((a, b) => {
      // Covered topics go to the bottom
      if (a.status === 'covered' && b.status !== 'covered') return 1;
      if (a.status !== 'covered' && b.status === 'covered') return -1;
      // Then by deadline (earliest first, no deadline last)
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      if (a.deadline && !b.deadline) return -1;
      if (!a.deadline && b.deadline) return 1;
      return 0;
    });
  }, [topics]);

  return (
    <List disablePadding sx={{ p: 0.5 }}>
      {topics.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No topics yet</Typography>
        </Box>
      )}
      {sortedTopics.map((topic, index) => {
        const planner = topic.assigned_planner_id ? plannerMap[topic.assigned_planner_id] : null;
        const isCovered = topic.status === 'covered';
        const isSelected = selectedTopicId === topic.id;
        const isLast = index === sortedTopics.length - 1;
        return (
          <ListItemButton
            key={topic.id}
            selected={isSelected}
            onClick={() => onSelect(topic)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              flexDirection: 'column',
              alignItems: 'stretch',
              py: 1.5,
              px: 2,
              position: 'relative',
              opacity: isCovered ? 0.55 : 1,
              '&::after': isLast ? {} : {
                content: '""',
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: 0,
                height: '1px',
                bgcolor: 'grey.200',
              },
              textDecoration: isCovered ? 'none' : 'none',
              borderLeft: isSelected ? '3px solid' : '3px solid transparent',
              borderLeftColor: isSelected ? 'primary.main' : 'transparent',
              '&.Mui-selected': {
                backgroundColor: 'rgba(176,122,143,0.12)',
                boxShadow: 'inset 0 0 0 1px rgba(176,122,143,0.2)',
                '&:hover': { backgroundColor: 'rgba(176,122,143,0.18)' },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {topic.emoji && <span style={{ fontSize: 18 }}>{topic.emoji}</span>}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  flexGrow: 1,
                  textDecoration: isCovered ? 'line-through' : 'none',
                  color: isCovered ? 'text.secondary' : 'text.primary',
                }}
              >
                {topic.name}
              </Typography>
              <StatusChip status={topic.status} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {topic.deadline && (
                <Typography variant="caption" color="text.secondary">
                  {formatDate(topic.deadline)}
                </Typography>
              )}
              {planner && (
                <Chip
                  label={planner.name}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: planner.color ? `${planner.color}25` : 'grey.100',
                    color: planner.color || 'grey.800',
                    border: `1px solid ${planner.color || '#E4DED8'}`,
                  }}
                />
              )}
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );
};

export default TopicList;
